import { format, isAfter, addHours } from 'date-fns';

const ACTIVE_TASK_STATUSES = new Set(['assigned', 'in-progress', 'pending']);
const CLOSED_TASK_STATUSES = new Set([
  'completed',
  'cancelled',
  'canceled',
  'rejected',
]);

export const VEHICLE_ASSIGNMENT_LOCK_HOURS = 4;

const normalizeTaskStatus = (status = '') =>
  String(status || '')
    .trim()
    .toLowerCase();

export const normalizeVehicleStatus = (status = '') =>
  String(status || '')
    .trim()
    .toLowerCase();

const normalizeVehicleSource = (vehicleSource = '') => {
  const normalizedSource = String(vehicleSource || '')
    .trim()
    .toLowerCase();

  if (normalizedSource === 'garage' || normalizedSource === 'fleet') {
    return 'warehouse';
  }

  return normalizedSource;
};

export const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (value?.toDate && typeof value.toDate === 'function') {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getTaskStartDate = (task = {}) =>
  parseDateValue(task.startTime || task.inspectionDate || task.taskDate || null);

const getTaskEndDate = (task = {}) =>
  parseDateValue(
    task.endTime ||
      task.inspectionEndTime ||
      task.startTime ||
      task.inspectionDate ||
      task.taskDate ||
      null
  );

const toFiniteNumber = (value, fallbackValue = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallbackValue;
};

const isVehicleManualUnavailable = (normalizedVehicleStatus = '') =>
  normalizedVehicleStatus === 'maintenance' ||
  normalizedVehicleStatus === 'unavailable';

export const getVehicleAssignmentState = (
  vehicle = null,
  tasks = [],
  options = {}
) => {
  const now = parseDateValue(options.now) || new Date();
  const lockHours = Math.max(
    0,
    toFiniteNumber(options.lockHours, VEHICLE_ASSIGNMENT_LOCK_HOURS)
  );
  const lockWindowMs = lockHours * 60 * 60 * 1000;

  const vehicleId = String(vehicle?.id || '').trim();
  const normalizedStatus = normalizeVehicleStatus(vehicle?.status);

  if (!vehicleId) {
    return {
      isAvailable: false,
      isTaskBlocked: false,
      isManualBlocked: true,
      effectiveStatus: normalizedStatus || 'unavailable',
      blockingReason: 'missing_vehicle_id',
      blockingTask: null,
      nextTask: null,
      availableUntil: null,
      lockHours,
    };
  }

  const relatedTasks = (Array.isArray(tasks) ? tasks : [])
    .filter((task) => {
      if (!task) {
        return false;
      }

      const taskVehicleId = String(task.vehicleId || '').trim();
      if (!taskVehicleId || taskVehicleId !== vehicleId) {
        return false;
      }

      const normalizedSource = normalizeVehicleSource(
        task.vehicleSource || 'warehouse'
      );

      return normalizedSource !== 'own';
    })
    .map((task) => ({
      task,
      status: normalizeTaskStatus(task.status),
      startDate: getTaskStartDate(task),
      endDate: getTaskEndDate(task),
    }))
    .filter(({ status }) => !CLOSED_TASK_STATUSES.has(status))
    .sort((firstTaskEntry, secondTaskEntry) => {
      const firstTime =
        firstTaskEntry.startDate?.getTime() ||
        firstTaskEntry.endDate?.getTime() ||
        Number.MAX_SAFE_INTEGER;
      const secondTime =
        secondTaskEntry.startDate?.getTime() ||
        secondTaskEntry.endDate?.getTime() ||
        Number.MAX_SAFE_INTEGER;

      return firstTime - secondTime;
    });

  let blockingTaskEntry = null;
  let blockingReason = '';

  for (const taskEntry of relatedTasks) {
    if (taskEntry.status === 'in-progress') {
      blockingTaskEntry = taskEntry;
      blockingReason = 'in_progress';
      break;
    }

    if (!ACTIVE_TASK_STATUSES.has(taskEntry.status)) {
      continue;
    }

    if (!taskEntry.startDate) {
      blockingTaskEntry = taskEntry;
      blockingReason = 'missing_schedule';
      break;
    }

    if (taskEntry.startDate <= now) {
      blockingTaskEntry = taskEntry;
      blockingReason = 'overdue_pending';
      break;
    }

    const assignmentLockStart = new Date(
      taskEntry.startDate.getTime() - lockWindowMs
    );

    if (now >= assignmentLockStart) {
      blockingTaskEntry = taskEntry;
      blockingReason = 'upcoming_lock_window';
      break;
    }
  }

  const nextUpcomingTaskEntry = relatedTasks.find(
    (taskEntry) =>
      ACTIVE_TASK_STATUSES.has(taskEntry.status) &&
      taskEntry.startDate &&
      taskEntry.startDate > now
  );

  const availableUntil = nextUpcomingTaskEntry?.startDate
    ? new Date(nextUpcomingTaskEntry.startDate.getTime() - lockWindowMs)
    : null;

  const isManualBlocked = isVehicleManualUnavailable(normalizedStatus);
  const isTaskBlocked = Boolean(blockingTaskEntry);
  const isAvailable = !isManualBlocked && !isTaskBlocked;

  let effectiveStatus = normalizedStatus || 'available';
  if (isManualBlocked) {
    effectiveStatus = normalizedStatus || 'unavailable';
  } else if (blockingReason === 'in_progress') {
    effectiveStatus = 'in-progress';
  } else if (blockingReason === 'overdue_pending') {
    effectiveStatus = 'pending';
  } else if (isTaskBlocked) {
    effectiveStatus = 'assigned';
  } else if (
    effectiveStatus === 'assigned' ||
    effectiveStatus === 'ready' ||
    effectiveStatus === 'ready_to_assign'
  ) {
    effectiveStatus = 'available';
  }

  return {
    isAvailable,
    isTaskBlocked,
    isManualBlocked,
    effectiveStatus,
    blockingReason,
    blockingTask: blockingTaskEntry?.task || null,
    nextTask: nextUpcomingTaskEntry?.task || null,
    availableUntil,
    lockHours,
  };
};

export const isVehicleReadyForAssignment = (
  vehicle = null,
  tasks = [],
  options = {}
) => getVehicleAssignmentState(vehicle, tasks, options).isAvailable;

export const getVehicleEffectiveStatus = (
  vehicle = null,
  tasks = [],
  options = {}
) => getVehicleAssignmentState(vehicle, tasks, options).effectiveStatus;

// Format timestamp to readable date
export const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMM dd, yyyy hh:mm a');
  } catch (error) {
    return 'Invalid Date';
  }
};

// Format date for input fields
export const formatDateForInput = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, "yyyy-MM-dd'T'HH:mm");
  } catch (error) {
    return '';
  }
};

// Check if driver is available in next 24 hours
export const isDriverAvailableIn24Hours = (driverOrAvailableUntil) => {
  // Handle new availabilitySchedule system
  if (driverOrAvailableUntil && driverOrAvailableUntil.availabilitySchedule && Array.isArray(driverOrAvailableUntil.availabilitySchedule)) {
    const now = new Date();
    const next24Hours = addHours(now, 24);
    
    return driverOrAvailableUntil.availabilitySchedule.some(avail => {
      if (!avail.isAvailable) return false;
      
      const endDate = avail.endDate?.toDate ? avail.endDate.toDate() : new Date(avail.endDate);
      return isAfter(endDate, now) && !isAfter(endDate, next24Hours);
    });
  }
  
  // Fallback to availableUntil (old system)
  const availableUntil = driverOrAvailableUntil?.availableUntil || driverOrAvailableUntil;
  if (!availableUntil) return false;
  
  try {
    const availableDate = availableUntil.toDate ? availableUntil.toDate() : new Date(availableUntil);
    const next24Hours = addHours(new Date(), 24);
    return isAfter(availableDate, new Date()) && !isAfter(availableDate, next24Hours);
  } catch (error) {
    return false;
  }
};

// Get status badge color
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'available':
    case 'completed':
      return 'success';
    case 'pending':
    case 'in-progress':
      return 'warning';
    case 'rejected':
    case 'unavailable':
    case 'cancelled':
      return 'error';
    case 'assigned':
      return 'info';
    default:
      return 'default';
  }
};

export const formatStatusLabel = (status, fallback = 'Unknown') => {
  const normalized = String(status || '').trim();

  if (!normalized) {
    return fallback;
  }

  return normalized
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Validate email
export const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

// Validate phone number
export const isValidPhone = (phone) => {
  const re = /^\+?[\d\s-()]{10,}$/;
  return re.test(phone);
};

// Generate unique driver ID
export const generateDriverId = () => {
  const prefix = 'DRV';
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}-${random}`;
};

// Generate unique vehicle ID
export const generateVehicleId = () => {
  const prefix = 'VEH';
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}-${random}`;
};

// Calculate statistics
export const calculateStats = (drivers = [], vehicles = [], tasks = []) => {
  const totalDrivers = drivers.length;
  const pendingApprovals = drivers.filter(d => !d.approved && d.status !== 'rejected').length;
  const approvedDrivers = drivers.filter(d => d.approved).length;
  const availableVehicles = vehicles.filter((vehicle) =>
    isVehicleReadyForAssignment(vehicle, tasks)
  ).length;
  const availableIn24h = drivers.filter(d => d.approved && isDriverAvailableIn24Hours(d)).length;
  const activeTasks = tasks.filter((task) =>
    ACTIVE_TASK_STATUSES.has(normalizeTaskStatus(task.status))
  ).length;

  return {
    totalDrivers,
    pendingApprovals,
    approvedDrivers,
    availableVehicles,
    availableIn24h,
    activeTasks
  };
};

// Format phone number for display
export const formatPhoneNumber = (phone) => {
  if (!phone) return 'N/A';
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
};

export const VEHICLE_TYPES = [
  'Exotic Vehicle',
  'Prestige Sedan',
  'Executive Sedan',
  'Luxury SUV',
  'EV Sedan',
  'Luxury People Mover',
  'Stretch Limousine',
  'Executive Mini Bus',
  'Bus',
  'Coach',
];

export const VEHICLE_MODELS = [
  { label: 'Bentley Bentayga — Max 3', value: 'Bentley Bentayga' },
  { label: 'Mercedes G-Wagon — Max 3', value: 'Mercedes G-Wagon' },
  { label: 'BMW 7 Series — Max 3', value: 'BMW 7 Series' },
  { label: 'Mercedes S-Class — Max 3', value: 'Mercedes S-Class' },
  { label: 'BMW 5 Series — Max 3', value: 'BMW 5 Series' },
  { label: 'BMW X7 — Max 3', value: 'BMW X7' },
  { label: 'Tesla Model Y — Max 3', value: 'Tesla Model Y' },
  { label: 'Mercedes V250D — Max 7 (5 luggage)', value: 'Mercedes V250D' },
  { label: 'Ford Fairlane Stretch — 10–15', value: 'Ford Fairlane Stretch' },
  { label: 'Mercedes Sprinter — 12–15', value: 'Mercedes Sprinter' },
  { label: 'Mitsubishi Rosa — 23', value: 'Mitsubishi Rosa' },
  { label: 'Yutong Coach', value: 'Yutong Coach' },
];
